import { CalendarItemType, Image } from '@akin-home/amplify';
import { OpenWeatherV2Response } from '@akin-home/weather';

export enum ChatItemTypes {
  TEXT = 'text',
  USER = 'user',
  OPTIONS = 'options',
  IMAGE = 'image',
  CALENDAR_ITEM = 'calendar_item',
  CALENDAR_ITEM_MAP = 'calendar_item_map',
  CHECKLIST = 'checklist',
  RECIPE_ITEM = 'recipe',
  TIMER = 'timer',
  WEATHER = 'weather',
  CLOCK = 'clock',
}

/** Base interface for Chat Items */
export interface ChatItemBase {
  type: ChatItemTypes;
  data?: any;

  /** This is being used in the new  */
  commands?: any[];

  /** Date/time of then the chat item was added */
  timestamp: string;
}

/** A chat message sent by the user */
export interface UserChatItem extends ChatItemBase {
  type: ChatItemTypes.USER;
  text: string;
}

/** Basic chat text item. Can be from either user or server */
export interface ChatTextItem extends ChatItemBase {
  type: ChatItemTypes.TEXT;

  data: {
    text: string;
    source: 'client' | 'server';
    image_url?: string;
    multiple_choice_options?: boolean;
    options?: ChatResponseOption[];

    /** An item can be disabled for a number of reasons. */
    disabled?: boolean;
  };
}

export interface ChatResponseOption {
  label: string;
  icon?: string;
  input: { type: string; value: string; state_variable?: string };
  selected?: boolean;
  color?: string;
}

/** Displays the weather in the chat */
export interface ChatWeatherItem extends ChatItemBase {
  type: ChatItemTypes.WEATHER;

  data: {
    weather_data: OpenWeatherV2Response;
  };
}

/** Displays the clock in the chat */
export interface ChatClockItem extends ChatItemBase {
  type: ChatItemTypes.CLOCK;

  data: {
    location?: string;
    date?: string;
    time?: string;
  };
}

/** Displays a set of selectable options in the chat */
export interface ChatOptionsItem extends ChatItemBase {
  type: ChatItemTypes.OPTIONS;

  data: {
    /** The options to be displayed */
    options: ChatOption[];
    /** The selected value */
    selected?: number;
    description?: string;
  };
}

export type ChatOption = { label: string; value: string };

/** Displays an image in the chat */
export interface ChatImageItem extends ChatItemBase {
  type: ChatItemTypes.IMAGE;

  data: {
    title?: string;
    description?: string;
    source: string;
  };
}

export interface ChatCalendarItemData {
  items: { item_id: string; type: CalendarItemType; recurrence_id?: string }[];
}

export interface ChatRecipeItemData {
  items: {
    item_id: string;
    name: string;
    image: Image | undefined;
    special_diets?: string[] | undefined;
  }[];
}

/** Displays a calendar item in the chat */
export interface ChatCalendarItem extends ChatItemBase {
  type: ChatItemTypes.CALENDAR_ITEM;
  data: ChatCalendarItemData;
}

/** Displays a checklist in the chat */
export interface ChatCheckListItem extends ChatItemBase {
  type: ChatItemTypes.CHECKLIST;
  data: {
    id: string;
  };
}

export interface ChatRecipeItem extends ChatItemBase {
  image?: Image;
  type: ChatItemTypes.RECIPE_ITEM;
  data: ChatRecipeItemData;
}

export type ChatItem =
  | UserChatItem
  | ChatTextItem
  | ChatCalendarItem
  | ChatCheckListItem
  | ChatImageItem
  | ChatOptionsItem
  | ChatRecipeItem
  | ChatWeatherItem
  | ChatClockItem;
